import {
  CategoryBarChart,
  ChartCard,
  DonutChart,
  foldSeries,
  formatNumber,
  KpiTile,
  ShareRows,
  useSeries,
} from '@lms/charts';
import { useMemo, useState } from 'react';
import { useLocale, useTranslations } from 'use-intl';
import { MultiSelect } from '@/components/multi-select';
import { SearchFilter } from '@/components/search-filter';
import type { Site } from '@/features/branding/brand-provider';
import { CompletedTrainingsCard } from '@/features/dashboard/components/completed-trainings-card';
import { DownloadStatsLink } from '@/features/dashboard/components/download-stats-link';
import { EducationTotal } from '@/features/dashboard/components/education-total';
import { PeriodSelector } from '@/features/dashboard/components/period-selector';
import {
  StatsGrid,
  StatsPlaceholder,
  StatsToolbar,
  usePeriodLabel,
  WIDE,
} from '@/features/dashboard/components/stats-layout';
import {
  locationSharesByType,
  totalWithCompletions,
  useLocationCoverage,
} from '@/features/dashboard/coverage';
import {
  averageCurriculumMinutes,
  curriculumMinutesAcrossLocations,
  curriculumTotalSeconds,
} from '@/features/dashboard/education-time';
import { type StatsHookParams, useStats } from '@/features/dashboard/use-stats';

type Session = { count: number; curriculumName: string; locationName: string };

const curriculumOf = (r: { curriculumName: string }) => r.curriculumName;
const locationOf = (r: { locationName: string }) => r.locationName;
const countOf = (r: { count: number }) => r.count;

/** Dashboard for a portfolio site: totals across its locations. */
export function PortfolioStats({
  site,
  locations: allLocations,
}: {
  site: Site;
  locations: { id: number; name: string }[];
}) {
  const t = useTranslations('admin');
  const t_stats = useTranslations('stats');
  const locale = useLocale();

  const [filter, setFilter] = useState<StatsHookParams>({
    locations: [site.id],
    courses: [],
    period: 'year',
    fromDate: new Date(),
  });

  // Each location has its own curricula; the filter picks them by name, so
  // choosing "Fire safety" covers it at every location (as the charts do).
  const [curriculumNames, setCurriculumNames] = useState<string[]>([]);
  const [curriculumList, setCurriculumList] = useState<
    { id: number; name: string }[]
  >([]);
  const curriculumIds = useMemo(
    () =>
      curriculumList
        .filter((c) => curriculumNames.includes(c.name))
        .map((c) => c.id),
    [curriculumList, curriculumNames],
  );
  // One entry per name, keyed by its lowest id.
  const curriculumOptions = useMemo(() => {
    const byName = new Map<string, number>();
    for (const c of curriculumList) {
      byName.set(c.name, Math.min(c.id, byName.get(c.name) ?? c.id));
    }
    return [...byName.entries()]
      .map(([label, id]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label, locale));
  }, [curriculumList, locale]);

  // Clicking a curriculum in a chart filters to it; clicking it again (when
  // it's the only one selected) clears the filter.
  const selectCurriculum = (name: string) =>
    setCurriculumNames((names) =>
      names.length === 1 && names[0] === name ? [] : [name],
    );

  const stats = useStats({ ...filter, curricula: curriculumIds });
  const { sessions, educationTime, isLoading, error } = stats;
  const coverage = useLocationCoverage(
    filter.locations,
    { period: filter.period, fromDate: filter.fromDate },
    curriculumIds,
  );
  const periodLabel = usePeriodLabel(filter.period, filter.fromDate);

  // One colour per curriculum, shared by every chart on the page. Curricula
  // that only appear in the education-time data still get a colour.
  const curriculumRows = useMemo<Session[]>(
    () => [...sessions, ...educationTime.map((e) => ({ ...e, count: 0 }))],
    [sessions, educationTime],
  );
  const curricula = useSeries(curriculumRows, curriculumOf, countOf, {
    otherLabel: t_stats('other'),
  });
  const donutCurricula = useMemo(
    () => foldSeries(curricula, 6, t_stats('other')),
    [curricula, t_stats],
  );
  // One bar per curriculum: the average location's curriculum time.
  const minutes = useMemo(
    () => curriculumMinutesAcrossLocations(educationTime),
    [educationTime],
  );
  const averageMinutes = useMemo(
    () => averageCurriculumMinutes(minutes),
    [minutes],
  );
  const timeTotals = useMemo(
    () => curriculumTotalSeconds(educationTime),
    [educationTime],
  );

  // The response lists every curriculum at the selected locations, whatever
  // the curriculum filter; keep the last list while a new one loads.
  if (
    stats.curricula.length > 0 &&
    (stats.curricula.length !== curriculumList.length ||
      stats.curricula.some((c, i) => c.id !== curriculumList[i]?.id))
  ) {
    setCurriculumList(stats.curricula);
  }

  const toolbar = (
    <StatsToolbar>
      <div className="w-full lg:max-w-72">
        <MultiSelect
          onSelect={(locations) => setFilter((f) => ({ ...f, locations }))}
          options={allLocations.map(({ id, name }) => ({
            value: id,
            label: name,
          }))}
          prefix={t('locations')}
        />
      </div>
      {curriculumOptions.length > 0 && (
        <div className="w-full lg:max-w-72">
          <SearchFilter
            prefix={t('curriculums')}
            sections={[{ items: curriculumOptions }]}
            selected={curriculumOptions
              .filter((o) => curriculumNames.includes(o.label))
              .map((o) => o.id)}
            onChange={(ids) =>
              setCurriculumNames(
                curriculumOptions
                  .filter((o) => ids.includes(o.id))
                  .map((o) => o.label),
              )
            }
            searchPlaceholder={t('search')}
            emptyText={t('no_matches')}
          />
        </div>
      )}
      <div className="w-full lg:max-w-72">
        <PeriodSelector
          startDate={new Date('2024-01-01T00:00:00+00:00')}
          onChange={(period, fromDate) =>
            setFilter((f) => ({ ...f, fromDate, period }))
          }
        />
      </div>
    </StatsToolbar>
  );

  if (filter.locations.length === 0) {
    return (
      <>
        {toolbar}
        <StatsPlaceholder state="empty" />
      </>
    );
  }
  const loadError = error ?? coverage.error;
  if (isLoading || coverage.isLoading || loadError) {
    return (
      <>
        {toolbar}
        <StatsPlaceholder
          state={loadError ? 'error' : 'loading'}
          error={loadError}
        />
      </>
    );
  }

  // Groups with completions in the selected period, per location.
  const byLocation = coverage.data;
  const tenants = byLocation
    ? totalWithCompletions(byLocation, 'tenant')
    : undefined;
  const suppliers = byLocation
    ? totalWithCompletions(byLocation, 'supplier')
    : undefined;
  const hasSuppliers = (suppliers?.groups ?? 0) > 0;
  const tenantLabel = t(site.groupAlias);
  const supplierLabel = t('suppliers');
  const shares = byLocation
    ? locationSharesByType(byLocation, [
        { type: 'tenant', label: tenantLabel },
        ...(hasSuppliers
          ? [{ type: 'supplier' as const, label: supplierLabel }]
          : []),
      ])
    : [];

  const kpis = [
    tenants && (
      <KpiTile
        key="tenants"
        label={tenantLabel}
        description={t_stats('with_completions')}
        value={tenants.withCompletions}
        of={tenants.groups}
        locale={locale}
      />
    ),
    suppliers && hasSuppliers && (
      <KpiTile
        key="suppliers"
        label={supplierLabel}
        description={t_stats('with_completions')}
        value={suppliers.withCompletions}
        of={suppliers.groups}
        locale={locale}
      />
    ),
  ].filter(Boolean);

  return (
    <>
      {toolbar}
      <StatsGrid>
        <ChartCard
          title={t_stats('completed_trainings')}
          description={periodLabel}
        >
          <DonutChart
            data={sessions}
            name={curriculumOf}
            value={countOf}
            seriesSet={donutCurricula}
            centerLabel={t_stats('trainings_lower')}
            locale={locale}
            emptyLabel={t_stats('no_data')}
            onSelect={selectCurriculum}
          />
        </ChartCard>

        {kpis.length > 0 && (
          <div className="grid auto-rows-fr gap-4">{kpis}</div>
        )}

        <ChartCard
          title={t_stats('course_time')}
          description={periodLabel}
          value={
            <EducationTotal
              totals={timeTotals}
              seriesSet={curricula}
              locale={locale}
            />
          }
          className="md:col-span-2 lg:col-span-1"
        >
          <CategoryBarChart
            data={minutes}
            category={curriculumOf}
            value={(m) => m.minutes}
            seriesSet={curricula}
            colorBy="category"
            valueFormatter={(v) =>
              t_stats('minutes_value', { value: formatNumber(v, locale) })
            }
            reference={
              averageMinutes > 0 && minutes.length > 1
                ? {
                    value: averageMinutes,
                    label: `${t_stats('average')} ${averageMinutes} min`,
                  }
                : undefined
            }
            locale={locale}
            labelWidth={104}
            emptyLabel={t_stats('no_data')}
            totalLabel={t_stats('total')}
            onSelect={selectCurriculum}
            footer={(name) => {
              const count = minutes.find(
                (m) => m.curriculumName === name,
              )?.locations;
              return count ? (
                <span className="text-muted-foreground">
                  {t_stats('average_of_locations', { count })}
                </span>
              ) : null;
            }}
          />
        </ChartCard>

        <CompletedTrainingsCard
          sessions={sessions}
          seriesSet={curricula}
          filter={filter}
          periodLabel={periodLabel}
        />

        {byLocation && (
          <ChartCard
            title={t_stats('per_location_with_title')}
            className={WIDE}
          >
            <ShareRows rows={shares} locale={locale} />
          </ChartCard>
        )}

        <ChartCard
          title={t_stats('trainings_per_location')}
          description={periodLabel}
          className={WIDE}
        >
          <CategoryBarChart
            data={sessions}
            category={locationOf}
            stack={curriculumOf}
            value={countOf}
            seriesSet={curricula}
            limit={15}
            otherLabel={t_stats('other')}
            locale={locale}
            labelWidth={180}
            emptyLabel={t_stats('no_data')}
            totalLabel={t_stats('total')}
          />
        </ChartCard>
      </StatsGrid>
      <DownloadStatsLink site={site} />
    </>
  );
}
